import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type ApprovalStatus = "approved" | "pending" | "declined" | "deactivated";

interface ApprovalRow {
  is_visible: boolean | null;
  declined_at: string | null;
  deactivated_at: string | null;
}

export const useMyApprovalStatus = () => {
  const { user, loading: authLoading } = useAuth();

  const query = useQuery<ApprovalStatus>({
    queryKey: ["my-approval-status", user?.id],
    enabled: !!user && !authLoading,
    staleTime: 30_000,
    queryFn: async () => {
      if (!user) return "pending";
      const { data, error } = await supabase
        .from("profiles")
        .select("is_visible, declined_at, deactivated_at")
        .eq("id", user.id)
        .maybeSingle<ApprovalRow>();

      if (error) throw error;
      if (!data) return "pending";
      if (data.deactivated_at) return "deactivated";
      if (data.declined_at) return "declined";
      if (data.is_visible) return "approved";
      return "pending";
    },
  });

  return {
    status: query.data,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
};
